import { useRef, useState } from 'react';
import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import logger from '@eneco/common/log';
import { stripAccolades } from '@eneco/common/string';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { useFormsPublicV2PostB2BForm } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { Stack, TransitionOpacity } from '@eneco/sparky';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import { prepareRevolutionFormRequest } from './generic-form/request';
import { RevolutionNewsletterBackLink, RevolutionNewsletterForm } from './newsletter-form/RevolutionNewsletterForm';
import type { RevolutionRegisterToNewsletterFormContent } from './revolution-register-to-newsletter-form-content.types';
import { GENERIC_FORM_SLOTS, NEWSLETTER_FORM_SLOTS, resolveSlot } from './slots';

const newsletterFormSchema = yup.object({
  emailaddress1: yup.string().email().required(),
  recaptchaToken: yup.string().required(),
});

interface FormValues {
  emailaddress1: string;
  recaptchaToken: string;
  formId?: string;
  [key: string]: string | undefined;
}

/**
 * `RevolutionRegisterToNewsletterForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/RegisterToNewsletterForm/RevolutionRegisterToNewsletterForm.tsx`:
 * an email field beside the `jss-form` slot and a submit button, then the disclaimer and the reCAPTCHA
 * notice, posting a Revolution CRM payload; on success or failure the form gives way to the matching
 * slot and a "back" link that resets it.
 *
 * The `formId` comes from a `GenericFormInputHiddenField` in the `jss-form` slot. Without one, the
 * submit is logged as failed and nothing is sent, as in legacy.
 *
 * Known simplification: the legacy `useTracking` funnel events are dropped (`lib:tracking` is not in
 * `lib:cms-components`'s allowed dependencies), as in `NewsletterFormIntegratedWithRevolution.tsx`.
 * What is submitted is unaffected.
 */
const RevolutionRegisterToNewsletterForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<RevolutionRegisterToNewsletterFormContent>();
  const rendering = useRendering();
  const { label, businessUnit } = useDC();
  const emailField = content.emailAddressFormField;

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isTransitioning, setTransitioning] = useState(false);
  const { send, reset, isError, isSuccess } = useFormsPublicV2PostB2BForm();

  const methods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: typedYupResolver<FormValues>(newsletterFormSchema),
  });
  const {
    formState: { errors },
  } = methods;

  const formSlot = resolveSlot(rendering, GENERIC_FORM_SLOTS.main);
  const introSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.intro);
  const successSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.success);
  const errorSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.error);

  const submitForm: SubmitHandler<FormValues> = async values => {
    setTransitioning(true);

    try {
      if (values?.formId) {
        const requestBody = prepareRevolutionFormRequest(values.formId, values);

        await send({
          businessUnit,
          formId: stripAccolades(values.formId),
          label,
          requestBody,
          recaptchaEnterpriseToken: values.recaptchaToken,
        } as Parameters<typeof send>[0]);
      } else {
        throw new Error('Form ID is required as a hidden field');
      }
    } catch (error) {
      logger.error('KSK1W5', 'Failed to submit form', { error });
    } finally {
      setTimeout(() => {
        setTransitioning(false);
      }, 500);
    }
  };

  const getEmailErrorMessage = () => {
    const defaultErrorMessage = errors.emailaddress1?.message;
    const requiredMessage = emailField?.requiredMessage;
    const validationMessage = emailField?.validationMessage;

    if (errors.emailaddress1?.type === 'required') {
      return requiredMessage ?? defaultErrorMessage;
    } else if (errors.emailaddress1?.type === 'email' && validationMessage) {
      return validationMessage ?? defaultErrorMessage;
    }

    return undefined;
  };

  const formOutput = (
    <RevolutionNewsletterForm
      methods={methods}
      onSubmit={submitForm}
      recaptchaRef={recaptchaRef}
      emailField={emailField}
      emailError={getEmailErrorMessage()}
      nextButtonText={content.nextButtonText}
      disclaimer={adapter.toRichText(content.disclaimerContent)}>
      {formSlot ? <Placeholder name={formSlot} /> : null}
    </RevolutionNewsletterForm>
  );

  const showForm = !isSuccess && !isError;
  const showBackButton = isSuccess || isError;

  return (
    <TransitionOpacity in={!isTransitioning}>
      <Stack gap="6">
        {showForm && (
          <>
            {introSlot ? <Placeholder name={introSlot} /> : null}
            {formOutput}
          </>
        )}

        {isSuccess && successSlot ? <Placeholder name={successSlot} /> : null}

        {isError && errorSlot ? <Placeholder name={errorSlot} /> : null}

        {showBackButton && (
          <RevolutionNewsletterBackLink
            text={content.backButtonText}
            reset={reset}
            setTransitioning={setTransitioning}
          />
        )}
      </Stack>
    </TransitionOpacity>
  );
};

export default RevolutionRegisterToNewsletterForm;
